import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Canvas } from '@react-three/fiber'
import * as THREE from 'three'
import { ROTATE_QUARTER_TURNS_DEFAULT, SWAP_UV_DEFAULT, rotateTexture, swapMeshUV, setSRGBIfAvailable } from './three/textureUtils'
import { useTranslation } from 'react-i18next'
import { useAppStore } from '../stores/appStore'
import { setLanguage } from '../i18n'
import { buildApiUrl } from '../config'
import OptionsFetcher from './OptionsFetcher'
import AccessoriesFetcher from './AccessoriesFetcher'
import Overview from './Overview'
import Scene from './three/Scene'
import ZoomButtons from './ZoomButtons'
import RotationButtons from './RotationButtons'
import LArrow from '../icons/larrow'
import SizeSelector from './SizeSelector'
import { isBedOptionAllowed } from '../utils/bedOptionRestrictions'


// texture helpers moved to ./three/textureUtils

const TopLinks = () => {
  const { setCurrentView } = useAppStore()
  const { t } = useTranslation()
  
  const links = [
    {
      label: t('common.links.build-link'),
      action: () => setCurrentView(null)
    },
    {
      label: t('common.links.overview-link'),
      action: () => setCurrentView('overview')
    }
  ]

  return (
    <div className="p-4 flex gap-2">
      {links.map((link, index) => (
        <button 
          key={index}
          onClick={link.action}
          className="uppercase"
        >
          {link.label}
        </button>
      ))}
    </div>
  )
}

const PillLinks = () => {
  const { t } = useTranslation()
  const { setCurrentView } = useAppStore()
  const bedVariation = useAppStore((s) => s.bedConfig.bed.variation)
  
  const handlePillClick = (slug: string) => {
    setCurrentView(slug)
  }
  const links = [
    { label: t('options.textile'), slug: 'textiles' },
    { label: t('options.topper'), slug: 'toppers' },
    { label: t('options.legs'), slug: 'legs' },
    { label: t('options.headboard'), slug: 'headboards' },
    { label: t('options.bedside-table'), slug: 'bedside-tables' },
    { label: t('options.ottoman'), slug: 'ottomans' },
    // Accessories are only available for some beds; hide pill if bed variation reports none
    ...(bedVariation?.has_accessories
      ? [{ label: t('options.accessories'), slug: 'accessories' as const }]
      : []),
    // { label: t('options.lamp'), slug: 'lamps' },
  ].filter((link) => isBedOptionAllowed(bedVariation, link.slug))

  return (
    <div className="p-4 flex gap-2 mx-auto overflow-x-auto scrollbar-hide vertical-scroll snap-x snap-mandatory">
      {links.map((link, index) => (
        <button
          key={index}
          onClick={() => handlePillClick(link.slug)}
          className="snap-center h-10 px-6 py-[9px] bg-white rounded-[20px] outline-1 outline-offset-[-1px] outline-zinc-300 inline-flex items-center gap-2.5 text-Text-color text-base font-normal font-['Suisse_Int\'l'] leading-[14px] hover:bg-gray-50 transition-colors"
        >
          {link.label}
        </button>
      ))}
    </div>
  )
}

// Drawer component that slides up from bottom
interface DrawerProps {
  isExpanded: boolean
  onClose: () => void
  title: string
  children?: React.ReactNode
  bedTitle?: string
  currentView?: string | null
}

const Drawer = ({ isExpanded, onClose, title, children, bedTitle, currentView }: DrawerProps) => {
  const { t } = useTranslation()
  const drawerRef = useRef<HTMLDivElement>(null)
  const handleRef = useRef<HTMLDivElement>(null)
  const [windowHeight, setWindowHeight] = useState(window.innerHeight)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStartY, setDragStartY] = useState(0)
  const [currentHeight, setCurrentHeight] = useState<number | null>(null)
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])
  
  // Collapsed height: enough for title + pill links + handle
  const COLLAPSED_HEIGHT = 180
  // Expanded height: larger for overview, smaller for other views
  const isOverview = currentView === 'overview'
  const EXPANDED_HEIGHT = isOverview 
    ? Math.min(windowHeight * 0.85, windowHeight - 100) // Overview: 85% or almost full screen
    : Math.min(windowHeight * 0.5, 450) // Other views: 50% max 450px
  
  // Reset current height when expanded state changes (but not during drag)
  useEffect(() => {
    if (!isDragging) {
      setCurrentHeight(null)
    }
  }, [isExpanded, isDragging, currentView])
  
  // Calculate current drawer height
  const getDrawerHeight = () => {
    if (currentHeight !== null) return currentHeight
    return isExpanded ? EXPANDED_HEIGHT : COLLAPSED_HEIGHT
  }
  
  // Handle drag start
  const handleDragStart = (clientY: number) => {
    if (!isExpanded) return // Only allow dragging when expanded
    setIsDragging(true)
    setDragStartY(clientY)
    setCurrentHeight(EXPANDED_HEIGHT)
  }
  
  // Handle drag move
  const handleDragMove = (clientY: number) => {
    if (!isDragging || !isExpanded) return
    
    const deltaY = clientY - dragStartY
    const newHeight = Math.max(COLLAPSED_HEIGHT, EXPANDED_HEIGHT - deltaY)
    setCurrentHeight(newHeight)
  }
  
  // Handle drag end
  const handleDragEnd = () => {
    if (!isDragging) return
    
    const finalHeight = currentHeight || EXPANDED_HEIGHT
    const threshold = (EXPANDED_HEIGHT + COLLAPSED_HEIGHT) / 2
    
    // If dragged down more than halfway, close the drawer
    if (finalHeight < threshold) {
      onClose()
    }
    
    setIsDragging(false)
    setCurrentHeight(null)
  }
  
  // Mouse events
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    handleDragStart(e.clientY)
  }
  
  const handleMouseMove = (e: MouseEvent) => {
    handleDragMove(e.clientY)
  }
  
  const handleMouseUp = () => {
    handleDragEnd()
  }
  
  // Touch events
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches[0]) {
      handleDragStart(e.touches[0].clientY)
    }
  }
  
  const handleTouchMove = (e: TouchEvent) => {
    if (e.touches[0]) {
      handleDragMove(e.touches[0].clientY)
    }
  }
  
  const handleTouchEnd = () => {
    handleDragEnd()
  }
  
  // Add global event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.addEventListener('touchmove', handleTouchMove, { passive: false })
      document.addEventListener('touchend', handleTouchEnd)
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        document.removeEventListener('touchmove', handleTouchMove)
        document.removeEventListener('touchend', handleTouchEnd)
      }
    }
  }, [isDragging, dragStartY, currentHeight])
  // Remove occurrences of patterns like "SC25", "MC25" etc:
  // exactly: one letter, then "C", then two digits (e.g. [A-Z]C\d{2})
  const cleanBedTitle = bedTitle
    ? bedTitle.replace(/\b[a-z]c\d{2}\b/gi, '').replace(/\s{2,}/g, ' ').trim()
    : bedTitle

  return (
    <div
      ref={drawerRef}
      className={`fixed bottom-0 left-0 right-0 bg-[#ebe7e1] shadow-lg z-50 ${
        isDragging ? '' : 'transition-all duration-500 ease-out'
      } ${isExpanded ? 'shadow-2xl' : 'shadow-md'}`}
      style={{
        height: `${getDrawerHeight()}px`,
        maxHeight: isOverview ? '90vh' : '60vh',
      }}
    >
      {/* Drag handle - only show when expanded */}
      {isExpanded && (
        <div 
          ref={handleRef}
          className="flex justify-center pt-2 pb-1 cursor-grab active:cursor-grabbing rounded-2xl"
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <div className="w-12 h-2 bg-gray-400 rounded-full"></div>
        </div>
      )}

      {/* Collapsed content - only visible when not expanded */}
      {!isExpanded && (
        <div className="transition-opacity duration-300 opacity-100">
          <h1 className="text-center p-2 justify-center text-gray-900 text-lg font-medium font-['Suisse_Int\'l'] leading-[14px]">
            {t('common.build-your-bed')} {cleanBedTitle}
          </h1>
          <PillLinks />
        </div>
      )}

      {/* Expanded content - only visible when expanded */}
      {isExpanded && (
        <div className="h-full flex flex-col overflow-hidden">
          <div className="flex justify-between items-center p-6 flex-shrink-0">
            <button 
              onClick={onClose}
              className="flex items-center gap-2 text-base leading-3.5 font-medium text-[#666] w-24 z-10 relative"
            >
              <LArrow color="#666" /> {t('common.back')}
            </button>
            <h2 className="text-xl font-bold">{title}</h2>
            <span className="w-24"/>
          </div>
          <div className="flex-1 overflow-y-auto scrollbar-hide">
            {children}
          </div>
        </div>
      )}
    </div>
  )
}

// Scene extracted to ./three/Scene

export default function BedVisualizer() {
  const { 
    market, 
    lang, 
    bed, 
    currentView, 
    setCurrentView,
    loadBedVariation,
    bedConfig,
    isLoadingBedVariation
  } = useAppStore()
  
  // Destructure for easier access
  const { variation: bedVariation, selectedSize, currentModel } = bedConfig.bed
  const { t } = useTranslation()
  const canvasRef = useRef<HTMLDivElement>(null)
  
  // Check for noUI URL parameter - check on every render to catch URL changes
  const urlParams = new URLSearchParams(window.location.search)
  const noUI = urlParams.get('noUI') === 'true' || urlParams.get('noUI') === ''
  
  // Use bedVariation slug if available, otherwise fall back to URL bed parameter
  const bedSlug = bedVariation?.slug || bed
  
  const { data } = useQuery({
    queryKey: ['bed', bed, market, lang],
    queryFn: () => {
      return fetch(buildApiUrl(`bed-variations/${bed}/?market=${market}&lang=${lang}`)).then(res => res.json())
    },
    enabled: !!bed
  })
  
  // Load 3D models when bed changes
  useEffect(() => {
    if (bed && data?.slug) {
      loadBedVariation(data.slug)
    }
  }, [bed, data?.slug, loadBedVariation])

  useEffect(() => {
    if (lang) {
      setLanguage(lang)
    }
  }, [lang])

  // Calculate camera position based on aspect ratio and device
  // Smaller viewports = more zoomed out (further camera distance)
  const [cameraConfig, setCameraConfig] = useState(() => {
    const width = window.innerWidth
    const isMobileDevice = width < 768
    const isTablet = width >= 768 && width < 1024
    
    // Adjust camera based on viewport size - smaller = more zoomed out
    if (isMobileDevice) {
      // Mobile: more zoomed out (further away)
      return { position: [0, 15, 28] as [number, number, number], fov: 32 }
    } else if (isTablet) {
      // Tablet: medium zoom
      return { position: [0, 15, 26] as [number, number, number], fov: 30 }
    } else {
      // Desktop: default/closer
      return { position: [0, 15, 25] as [number, number, number], fov: 30 }
    }
  })
  
  // Update camera on resize
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth
      const isMobileDevice = width < 768
      const isTablet = width >= 768 && width < 1024
      
      if (isMobileDevice) {
        setCameraConfig({ position: [0, 15, 28], fov: 32 })
      } else if (isTablet) {
        setCameraConfig({ position: [0, 15, 26], fov: 30 })
      } else {
        setCameraConfig({ position: [0, 15, 25], fov: 30 })
      }
    }
    
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  if (!bed) {
    return <div className="p-4">No bed selected</div>
  }

           const getModalTitle = (view: string) => {
           switch (view) {
             case 'textiles':
               return t('options.textile')
             case 'toppers':
               return t('options.topper')
             case 'legs':
               return t('options.legs')
             case 'headboards':
               return t('options.headboard')
             case 'bedside-tables':
               return t('options.bedside-table')
             case 'ottomans':
               return t('options.ottoman')
             case 'lamps':
               return t('options.lamp')
             case 'accessories':
               return t('options.accessories')
             case 'overview':
               return t('common.links.overview-link')
            
             default:
               return 'Unknown View'
           }
         }

         const getModalContent = (view: string) => {
           switch (view) {
             case 'textiles':
               return <OptionsFetcher bedId={bedSlug} optionType="textiles" />
             case 'toppers':
               return <OptionsFetcher bedId={bedSlug} optionType="toppers" />
             case 'legs':
               return <OptionsFetcher bedId={bedSlug} optionType="legs" />
             case 'headboards':
               return <OptionsFetcher bedId={bedSlug} optionType="headboards" />
             case 'bedside-tables':
               return <OptionsFetcher bedId={bedSlug} optionType="bedside-tables" />
             case 'ottomans':
               return <OptionsFetcher bedId={bedSlug} optionType="ottomans" />
             case 'lamps':
               return <OptionsFetcher bedId={bedSlug} optionType="lamps" />
             case 'accessories':
               return <AccessoriesFetcher bedId={bedSlug} />
             case 'overview':
               return <Overview />
             default:
               return <p>Unknown view: {view}</p>
           }
         }

  // Fixed canvas height - doesn't change when drawer expands
  const topLinksHeight = 80 // Approximate height of TopLinks
  const collapsedDrawerHeight = 180 // Fixed collapsed drawer height
  const fixedCanvasHeight = `calc(100vh - ${topLinksHeight}px - ${collapsedDrawerHeight}px)`
  
  // When noUI is enabled, use full screen height
  const canvasHeight = noUI ? '100vh' : fixedCanvasHeight

  return (
    <div className={noUI ? "h-screen w-screen overflow-hidden" : "min-h-screen"}>

      {/* Three.js Canvas - fixed height, drawer overlays when expanded */}
      {selectedSize ? (
        <>
        {!noUI && <TopLinks />}
        <div 
          ref={canvasRef} 
          className="w-full relative"
          style={{ 
            height: canvasHeight,
            minHeight: '400px'
          }}
        >
          <Canvas
            shadows="variance"
            camera={{ position: cameraConfig.position, fov: cameraConfig.fov }}
            gl={{ preserveDrawingBuffer: true, alpha: true }}
          >
            <Scene />
          </Canvas>
          {!noUI && <ZoomButtons />}
          {!noUI && <RotationButtons />}
        </div>
        </>
      ) : (
          !noUI && <SizeSelector />
      )}

      {/* Drawer - only show when size is selected and noUI is not enabled */}
      {selectedSize && !noUI && (
        <Drawer
          isExpanded={currentView !== null}
               onClose={() => setCurrentView(null)}
               title={currentView ? getModalTitle(currentView) : ''}
          bedTitle={data?.title}
          currentView={currentView}
             >
               {currentView && getModalContent(currentView)}
        </Drawer>
      )}
    </div>
  )
}
